import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { loginWithWorker, registerWithWorker, verifyGoogleTokenWithWorker, setStoredUser, apiGet } from '../lib/api';
import { Sparkles, Mail, Lock, LogIn, ChevronRight, CheckCircle2, User, RefreshCw, AlertCircle, Globe } from 'lucide-react';
import { AppLogo } from './AppLogo';
import { getAppLanguage, setAppLanguage, Language, translate } from '../lib/i18n';

function parseGoogleJwt(token: string) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      window.atob(base64).split('').map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

interface LoginScreenProps {
  onSuccess: (user: any) => void;
  onSkip?: () => void;
}

export default function LoginScreen({ onSuccess, onSkip }: LoginScreenProps) {
  const [currentLang, setCurrentLang] = useState<Language>(getAppLanguage());
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLangSelect = (lang: Language) => {
    setAppLanguage(lang);
    setCurrentLang(lang);
  };

  const DEFAULT_GOOGLE_CLIENT_ID = '937054100075-s2caqudl2n75of6f8g7oo665ejsusbb9.apps.googleusercontent.com';
  const envClientId = ((import.meta as any).env?.VITE_GOOGLE_CLIENT_ID as string) || '';
  const [googleClientId, setGoogleClientId] = useState<string>(() => {
    return envClientId || localStorage.getItem('progress_custom_google_client_id') || DEFAULT_GOOGLE_CLIENT_ID;
  });
  const [manualClientIdInput, setManualClientIdInput] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);

  // Optional dynamic client ID check (skip if already populated)
  React.useEffect(() => {
    if (googleClientId && googleClientId !== '') return;
    
    apiGet<any>('/api/config')
      .then(res => {
        if (res.success && res.data && res.data.googleClientId) {
          setGoogleClientId(res.data.googleClientId);
        }
      })
      .catch(() => {});
  }, [googleClientId]);

  const handleSaveManualClientId = (e: React.FormEvent) => {
    e.preventDefault();
    const cleaned = manualClientIdInput.trim();
    if (!cleaned) return;
    setGoogleClientId(cleaned);
    try {
      localStorage.setItem('progress_custom_google_client_id', cleaned);
    } catch (e) {}
    setShowManualInput(false);
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError(translate(currentLang, 'loginErrorFillAll', 'لطفا تمام فیلدها را پر کنید.'));
      return;
    }
    if (password.length < 6) {
      setError(translate(currentLang, 'loginErrorPassLength', 'رمز عبور باید حداقل ۶ کاراکتر باشد.'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isSignUp) {
        if (!displayName) {
          setError(translate(currentLang, 'loginErrorName', 'لطفا نام خود را وارد کنید.'));
          setLoading(false);
          return;
        }
        const res = await registerWithWorker(email, password, displayName);
        if (res.success) {
          onSuccess(res.user || { id: res.userId, email, displayName });
        } else {
          setError(res.error || translate(currentLang, 'loginErrorCreate', 'خطا در ایجاد حساب کاربری'));
        }
      } else {
        const res = await loginWithWorker(email, password);
        if (res.success && res.user) {
          onSuccess(res.user);
        } else {
          setError(res.error || translate(currentLang, 'loginErrorWrong', 'ایمیل یا رمز عبور اشتباه است.'));
        }
      }
    } catch (err: any) {
      console.error('Email Auth Error:', err);
      setError(translate(currentLang, 'loginErrorUnexpected', 'یک خطای غیرمنتظره رخ داد. لطفا مجددا تلاش کنید.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div dir={currentLang === 'fa' ? 'rtl' : 'ltr'} className="min-h-screen flex items-center justify-center bg-gradient-to-tr from-slate-50 to-indigo-50/40 dark:from-gray-950 dark:to-indigo-950/20 px-4 py-12 font-sans transition-colors duration-200">
      
      {/* Decorative Blur Background Circles */}
      <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-emerald-400/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-3xl border border-gray-100 dark:border-gray-800 shadow-2xl overflow-hidden p-8 space-y-6 relative z-10 animate-fadeIn">
        
        {/* Language Switcher Bar */}
        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/80 p-1.5 rounded-2xl border border-gray-100 dark:border-gray-800">
          <span className="text-[11px] font-bold text-gray-500 dark:text-gray-400 px-2 flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5 text-indigo-500" />
            <span>{translate(currentLang, 'language', 'زبان')}</span>
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => handleLangSelect('fa')}
              className={`px-2.5 py-1 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                currentLang === 'fa'
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              🇮🇷 فارسی
            </button>
            <button
              type="button"
              onClick={() => handleLangSelect('en')}
              className={`px-2.5 py-1 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                currentLang === 'en'
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              🇬🇧 English
            </button>
            <button
              type="button"
              onClick={() => handleLangSelect('sv')}
              className={`px-2.5 py-1 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                currentLang === 'sv'
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              🇸🇪 Svenska
            </button>
          </div>
        </div>

        {/* Brand Header */}
        <div className="flex flex-col items-center text-center space-y-3">
          <AppLogo className="w-20 h-20" />
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 dark:text-white font-display">
              {translate(currentLang, 'loginAppTitle', 'پیشرفت')}
            </h1>
            <p className="text-xs text-gray-600 dark:text-gray-300 font-medium leading-relaxed mt-1.5 max-w-xs mx-auto">
              {translate(currentLang, 'loginSlogan', 'سامانه جامع رشد شخصی، توانبخشی و ورزش، مدیریت هوشمند مالی و بودجه‌بندی فاکتورها، سلامت و پیگیری اهداف زندگی')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-1.5 mt-3">
              <span className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 rounded-full border border-emerald-200 dark:border-emerald-800 font-bold">
                {translate(currentLang, 'loginBadgeGrowth', '🌱 رشد و عادت‌ها')}
              </span>
              <span className="text-[10px] px-2 py-0.5 bg-blue-50 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 rounded-full border border-blue-200 dark:border-blue-800 font-bold">
                {translate(currentLang, 'loginBadgeRehab', '🩺 توانبخشی و سلامت')}
              </span>
              <span className="text-[10px] px-2 py-0.5 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/60 dark:text-indigo-300 rounded-full border border-indigo-200 dark:border-indigo-800 font-bold">
                {translate(currentLang, 'loginBadgeFinance', '💰 مدیریت مالی و فاکتور AI')}
              </span>
            </div>
          </div>
        </div>

        {/* Error Alert Box */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-start gap-2.5 text-xs text-red-600 dark:text-red-400 animate-slideDown">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <p className="leading-relaxed">{error}</p>
          </div>
        )}

        {/* Google OAuth (Google Identity Services) Popup Component */}
        <div className="space-y-3">
          <div className="w-full">
              {loading ? (
                <div className="flex items-center justify-center gap-2 text-xs text-gray-500 py-3 bg-gray-50 dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-800">
                  <RefreshCw className="w-4 h-4 animate-spin text-emerald-500" />
                  <span className="font-bold">{translate(currentLang, 'loginVerifyingGoogle', 'در حال تایید امن حساب گوگل...')}</span>
                </div>
              ) : googleClientId ? (
                <div className="p-[2px] rounded-3xl bg-gradient-to-r from-blue-500 via-red-500 via-yellow-500 to-green-500 shadow-md hover:shadow-xl transition-all duration-300">
                  <div className="bg-white dark:bg-gray-900 rounded-[22px] p-2.5 flex flex-col items-center justify-center google-btn-wrapper">
                    <GoogleLogin
                      onSuccess={async (credentialResponse) => {
                        if (credentialResponse.credential) {
                          setLoading(true);
                          setError(null);
                          try {
                            const googlePayload = parseGoogleJwt(credentialResponse.credential);
                            const googleName = googlePayload?.name || (googlePayload?.email ? googlePayload.email.split('@')[0] : '');
                            const googlePicture = googlePayload?.picture || '';
                            const googleEmail = googlePayload?.email || '';

                            const res = await verifyGoogleTokenWithWorker(credentialResponse.credential);
                            if (res.success && res.user) {
                              const isGenericName = (n?: string | null) => !n || ['کاربر', 'کاربر پیشرفت', 'پیشرفت', 'کاربر بدون نام', 'کاربر مهمان'].includes(n.trim());
                              
                              const bestName = !isGenericName(googleName) 
                                ? googleName 
                                : !isGenericName(res.user.displayName) 
                                ? res.user.displayName 
                                : !isGenericName(res.user.display_name) 
                                ? res.user.display_name 
                                : (googleEmail ? googleEmail.split('@')[0] : 'کاربر');

                              const bestPhoto = googlePicture || res.user.photoURL || res.user.photo_url || '';

                              const fullUser = {
                                ...res.user,
                                displayName: bestName,
                                display_name: bestName,
                                photoURL: bestPhoto,
                                photo_url: bestPhoto,
                                email: res.user.email || googleEmail
                              };
                              setStoredUser(fullUser);
                              onSuccess(fullUser);
                            } else {
                              setError(res.error || translate(currentLang, 'loginErrorGoogleToken', 'خطا در تایید حساب گوگل'));
                            }
                          } catch (err: any) {
                            setError(translate(currentLang, 'loginErrorServer', 'خطا در ارتباط با سرور.'));
                          } finally {
                            setLoading(false);
                          }
                        }
                      }}
                      onError={() => {
                        setError(translate(currentLang, 'loginGoogleError', 'ورود با حساب گوگل ناموفق بود یا توسط کاربر لغو شد.'));
                      }}
                      theme="outline"
                      size="large"
                      shape="pill"
                      width="320"
                      logo_alignment="center"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2 w-full">
                  <div className="text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 p-3 rounded-2xl text-center w-full border border-amber-500/20 flex flex-col items-center gap-1.5">
                    <span>{translate(currentLang, 'loginGoogleClientPrompt')}</span>
                    <button
                      type="button"
                      onClick={() => setShowManualInput(!showManualInput)}
                      className="text-[11px] underline font-bold text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 mt-1"
                    >
                      {showManualInput ? translate(currentLang, 'loginGoogleManualInputClose') : translate(currentLang, 'loginGoogleManualInputPrompt')}
                    </button>
                  </div>

                  {showManualInput && (
                    <form onSubmit={handleSaveManualClientId} className="p-3 bg-gray-50 dark:bg-gray-950 rounded-2xl border border-gray-200 dark:border-gray-800 space-y-2">
                      <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 block pr-1">
                        Google OAuth Client ID:
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="...apps.googleusercontent.com"
                        value={manualClientIdInput}
                        onChange={(e) => setManualClientIdInput(e.target.value)}
                        className="w-full text-xs p-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-xl focus:outline-none focus:border-emerald-500 dir-ltr text-left"
                      />
                      <button
                        type="submit"
                        className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-colors"
                      >
                        {translate(currentLang, 'loginGoogleManualInputSave')}
                      </button>
                    </form>
                  )}
                </div>
              )}
            </div>

          <div className="relative flex items-center justify-center py-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-100 dark:border-gray-800"></div>
            </div>
            <span className="relative px-3 bg-white dark:bg-gray-900 text-[10px] font-bold text-gray-400 uppercase">
              {translate(currentLang, 'loginEmailSeparator', 'یا از طریق ایمیل')}
            </span>
          </div>
        </div>

        {/* Email & Password Form */}
        <form onSubmit={handleEmailAuth} className="space-y-4">
          {isSignUp && (
            <div className="space-y-1">
              <label className="text-[10px] font-bold text-gray-400 block pr-1">
                {translate(currentLang, 'loginFullNameLabel', 'نام و نام خانوادگی')}
              </label>
              <div className="relative">
                <User className="absolute right-3.5 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  required
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={translate(currentLang, 'loginFullNamePlaceholder', 'مثال: سلیم زارع‌پور')}
                  className="w-full py-2.5 pl-4 pr-10 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-2xl text-sm focus:outline-none focus:border-emerald-500 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-[10px] font-bold text-gray-400 block pr-1">
              {translate(currentLang, 'loginEmailLabel', 'آدرس ایمیل')}
            </label>
            <div className="relative">
              <Mail className="absolute right-3.5 top-3 w-4 h-4 text-gray-400" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@gmail.com"
                className="w-full py-2.5 pl-4 pr-10 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-2xl text-sm focus:outline-none focus:border-emerald-500 text-gray-900 dark:text-white font-mono text-left"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-bold text-gray-400 block pr-1">
              {translate(currentLang, 'loginPasswordLabel', 'رمز عبور')}
            </label>
            <div className="relative">
              <Lock className="absolute right-3.5 top-3 w-4 h-4 text-gray-400" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••"
                className="w-full py-2.5 pl-4 pr-10 bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-2xl text-sm focus:outline-none focus:border-emerald-500 text-gray-900 dark:text-white font-mono"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-2xl transition-all shadow-md shadow-emerald-600/10 flex items-center justify-center gap-2 cursor-pointer"
          >
            {loading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <LogIn className="w-4 h-4" />
            )}
            <span>
              {isSignUp 
                ? translate(currentLang, 'loginSubmitNewAccount', 'ایجاد حساب کاربری جدید') 
                : translate(currentLang, 'loginSubmitSignIn', 'ورود به سیستم')}
            </span>
          </button>
        </form>

        {/* Toggle Signup/Signin */}
        <div className="flex flex-col gap-3 pt-2 text-center text-xs">
          <button
            onClick={() => setIsSignUp(!isSignUp)}
            className="text-indigo-500 dark:text-indigo-400 font-semibold hover:underline bg-transparent border-0 cursor-pointer"
          >
            {isSignUp 
              ? translate(currentLang, 'loginToggleToSignIn', 'حساب کاربری دارید؟ از اینجا وارد شوید') 
              : translate(currentLang, 'loginToggleToSignUp', 'هنوز حساب کاربری ندارید؟ ثبت‌نام رایگان')}
          </button>
        </div>

      </div>
    </div>
  );
}
